import { useMemo } from "react";

import noop from "lodash/noop";

import { useQueryParamsV6 } from "../hooks/useQueryParams/useQueryParams";
import { RVGrid } from "../RVGrid";
import { createEmptyRowsList, interceptColumnRenders } from "../utils/LoadingGridUtils/LoadingGridUtils";

import type { GridProps } from "../RVGridTypes";

export type RVLoadingGridProps = GridProps & {
    /**
     * Allows you to define the default height for the loading skeleton rows.
     * We're going with lines to try to standardize rather than allowing any number.
     */
    skeletonRowLines?: number;
};

/**
 * Wrapper for the shared Grid component, rendering skeleton text frames in individual cells rather
 * than a loading div when `loading` is true
 *
 * @remarks There's an issue doing this in an infinite scroll table right now that causes the table
 *          to load rows incorrectly, I think it may be due to row keys not being unique enough
 *
 * @param props - Table Props (see {@link RVGrid})
 */
export function RVLoadingGrid({ skeletonRowLines = 1, ...props }: RVLoadingGridProps): JSX.Element {
    const { queryParams } = useQueryParamsV6(props.defaultParams);
    const { loading } = props;

    const emptyRowsList = useMemo(() => createEmptyRowsList(queryParams), [queryParams]);
    const propsForGrid = loading
        ? {
              ...props,
              columns: interceptColumnRenders(props.columns, skeletonRowLines),
              columnsData: emptyRowsList,
              rowKey: (record: any) => record.rowKey,
              // Skeleton rows only carry `rowKey`, so consumer callbacks would run against records
              // missing every field they read
              rowClassName: noop,
              onRow: noop,
              // Overwrite false so the default loading div doesnt pop up
              loading: false,
              pagination: false,
          }
        : props;

    return <RVGrid {...propsForGrid} />;
}
